import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../../Api/api';
import { getAuthToken, getAuthUser, isAdminUser, isPartnerRoute } from '../../Api/authToken';
import { FiEye, FiEdit2, FiTrash2, FiSearch, FiRefreshCcw, FiChevronLeft, FiChevronRight, FiX, FiFilter } from 'react-icons/fi';
import './AdminPages.css';
import AdminPropertyLocationsMap from '../Components/AdminPropertyLocationsMap';
import { formatStateInCityFromIds } from '../../utils/locationFormat';
import { isCustomDropdownValue, decodeCustomDropdownValue } from '../../utils/dropdownCustomValue';
import staticDropdowns from '../../json/dropdown';

/** True when this listing was created by the currently logged-in Admin / Partner. */
const isCreatedByLoggedInStaff = (prop, staff, asPartner) => {
  if (!prop || !staff) return false;
  if (asPartner) {
    const pid = Number(staff.id);
    const raw = prop.partner_id;
    const creator = Array.isArray(raw) ? Number(raw[0]) : Number(raw);
    return Number.isFinite(pid) && pid > 0 && creator === pid;
  }
  const ids = [...new Set(
    [staff.id, staff.admin_id]
      .map((v) => Number(v))
      .filter((n) => Number.isFinite(n) && n > 0)
  )];
  const propAdminId = Number(prop.admin_id);
  return ids.length > 0 && ids.includes(propAdminId);
};

const matchFurnishingOption = (dropdown, value) => {
  const options = Array.isArray(dropdown?.options) ? dropdown.options : [];
  return options.find((o) => String(o.value) === String(value))
    || options.find((o) => o.id != null && Number(o.id) === Number(value))
    || options.find((o) => String(o.label || '').trim().toLowerCase() === String(value).trim().toLowerCase())
    || null;
};

const findFurnishingDropdown = (rows) => {
  const list = Array.isArray(rows) ? rows : [];
  return list.find((d) => Number(d.id) === 3)
    || list.find((d) => String(d.name || '').toLowerCase().includes('furnish'))
    || null;
};

const furnishingLabel = (value, dropdowns) => {
  if (value == null || value === '') return '';
  if (isCustomDropdownValue(value)) return decodeCustomDropdownValue(value);
  const opt = matchFurnishingOption(findFurnishingDropdown(dropdowns), value)
    || matchFurnishingOption(findFurnishingDropdown(staticDropdowns), value);
  return opt?.label || String(value);
};

const ListingToggle = ({ label, checked, onToggle }) => (
  <div className="listing-toggle-row">
    <span>{label}</span>
    <button
      type="button"
      className={`admin-pill-toggle${checked ? ' is-on' : ''}`}
      aria-pressed={!!checked}
      onMouseDown={(e) => e.preventDefault()}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onToggle();
      }}
    >
      <span className="admin-pill-toggle__text">{checked ? 'OFF' : 'ON'}</span>
      <span className="admin-pill-toggle__knob" aria-hidden="true">
        <svg className="admin-pill-toggle__chevron" viewBox="0 0 12 12" focusable="false">
          <path
            d={checked ? 'M8 2.15 L4 6 L8 9.85' : 'M4 2.15 L8 6 L4 9.85'}
            fill="none"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
    </button>
  </div>
);

/** Normalize listing role for display & row highlight: Admin | Partner | Owner | Agent | User. */
const resolveListingRoleBucket = (role, userListingRole) => {
  const r = String(role || '').trim().toLowerCase();
  if (r === 'admin' || r === 'sub_admin') return 'admin';
  if (r === 'partner') return 'partner';
  if (r === 'owner') return 'owner';
  if (r === 'agent') return 'agent';
  // User listings may store role as "user" — fall back to preferred listing role
  const ur = String(userListingRole || '').trim().toLowerCase();
  if (ur === 'owner') return 'owner';
  if (ur === 'agent') return 'agent';
  return 'user';
};

const listingRoleLabel = (role, userListingRole) => {
  const bucket = resolveListingRoleBucket(role, userListingRole);
  if (bucket === 'admin') return 'Admin';
  if (bucket === 'partner') return 'Partner';
  if (bucket === 'owner') return 'Owner';
  if (bucket === 'agent') return 'Agent';
  return 'User';
};

const ownerNumberTooltip = (item) => {
  const byRole = String(item.property_owner_number_added_by_role || '').trim();
  const byName = String(item.property_owner_number_added_by_name || '').trim();
  if (!byRole && !byName) return 'Owner number (attribution unknown)';
  const roleLabel = listingRoleLabel(byRole || 'admin');
  if (byName) return `Added by ${roleLabel}: ${byName}`;
  return `Added by ${roleLabel}`;
};

const isUserListingRole = (bucket) => bucket === 'owner' || bucket === 'agent' || bucket === 'user';

const PropertyList = ({ isAwaiting = false, isOwn = false, isMine = false, isSoldOut = false }) => {
  const navigate = useNavigate();
  const listMode = isAwaiting ? 'awaiting' : isSoldOut ? 'sold-out' : isMine ? 'mine' : isOwn ? 'own' : 'all';

  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [allCities, setAllCities] = useState([]);
  const [allSubRegisters, setAllSubRegisters] = useState([]);
  const [hoveredId, setHoveredId] = useState(null);
  const [ownerEditId, setOwnerEditId] = useState(null);
  const [ownerNameDraft, setOwnerNameDraft] = useState('');
  const [ownerNumberDraft, setOwnerNumberDraft] = useState('');
  const [savingOwnerId, setSavingOwnerId] = useState(null);
  const isAdmin = isAdminUser();


const displayListings = listings;

const [showPartnerModal, setShowPartnerModal] = useState(false);
const [selectedProperty, setSelectedProperty] = useState(null);
const [partners, setPartners] = useState([]);

useEffect(() => {
  const fetchPartners = async () => {
    try {
      const token = getAuthToken();
      const res = await axios.get(`${API_URL}/admin/partners`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPartners(Array.isArray(res.data) ? res.data : (Array.isArray(res.data?.data) ? res.data.data : []));
    } catch (err) {
      console.error('Failed to fetch partners');
      setPartners([]);
    }
  };

  fetchPartners();
}, []);

const getMatchingPartners = () => {
  if (!selectedProperty) return [];

const propertySubId = Number(
  selectedProperty?.extra_fields?.sub_register_id
);


  return partners.filter(p => {
    let subs = p.sub_registers;

    if (!subs) return false;

    // 🔥 normalize everything to array of numbers
    if (typeof subs === "string") {
      subs = subs.split(",");
    }

    if (!Array.isArray(subs)) return false;

    subs = subs.map(Number);

    return subs.includes(propertySubId);
  });
};
useEffect(() => {
  console.log("Selected Property:", selectedProperty);
  console.log("Partners:", partners);
  console.log("SubRegisters:", allSubRegisters);
}, [selectedProperty]);

const getSubRegisterNames = (subIds) => {
  let ids = subIds;
  if (typeof ids === 'string') {
    ids = ids.split(',').map((s) => s.trim()).filter(Boolean);
  }
  if (!Array.isArray(ids)) return [];
  return ids
    .map(id => {
      const sub = allSubRegisters.find(s => Number(s.id) === Number(id));
      return sub ? sub.sub_name : null;
    })
    .filter(Boolean);
};

const resolveListingThumb = (thumbnail) => {
  const t = typeof thumbnail === 'string' ? thumbnail.trim() : '';
  if (!t) return 'https://placehold.co/100x80?text=No+Image';
  if (/^https?:\/\//i.test(t)) return t;
  const baseUrl = API_URL.replace('/api', '');
  if (t.startsWith('uploads/')) return `${baseUrl}/${t}`;
  if (t.startsWith('/uploads/')) return `${baseUrl}${t}`;
  return `${baseUrl}/uploads/${t}`;
};




  // Filter States
  const initialFilters = useMemo(() => ({
    search: '',
    location_search: '',
    role: '',
    is_project: '',
    listing_type: '',
    status_id: isAwaiting ? '1' : (isSoldOut ? '4' : ''),
    is_urgent: '',
    is_highlighted: '',
    is_draft: '',
    state_id: '',
    city_id: '',
    sub_register_id: [],
    date_from: '',
    date_to: '',
    price_exact: '',
    price_min: '',
    price_max: '',
    page: 1,
    limit: 10,
  }), [isAwaiting, isSoldOut]);

  const [filters, setFilters] = useState(initialFilters);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [subRegisterQuery, setSubRegisterQuery] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const filterPanelRef = useRef(null);
  const hasLoadedRef = useRef(false);
  const [debouncedText, setDebouncedText] = useState({
    search: '',
    location_search: '',
    price_exact: '',
    price_min: '',
    price_max: '',
  });

  // Dropdown options
  const [statesList, setStatesList] = useState([]);
  const [citiesList, setCitiesList] = useState([]);
  const [subRegistersList, setSubRegistersList] = useState([]);
  const [dropdowns, setDropdowns] = useState([]);

  // Fetch initial states & global lookup data
  useEffect(() => {
    fetchStates();
    axios.get(`${API_URL}/cities`).then(r => setAllCities(Array.isArray(r.data) ? r.data : [])).catch(() => setAllCities([]));
    axios.get(`${API_URL}/subregisters`).then(r => setAllSubRegisters(Array.isArray(r.data) ? r.data : [])).catch(() => setAllSubRegisters([]));
    axios.get(`${API_URL}/dropdown`).then((r) => {
      const rows = r.data?.data || r.data || [];
      setDropdowns(Array.isArray(rows) ? rows : []);
    }).catch(() => setDropdowns([]));
  }, []);

  const queryFilters = useMemo(() => ({
    ...filters,
    search: debouncedText.search,
    location_search: debouncedText.location_search,
    price_exact: debouncedText.price_exact,
    price_min: debouncedText.price_min,
    price_max: debouncedText.price_max,
  }), [filters, debouncedText]);

  const fetchListings = useCallback(async (signal) => {
    if (!hasLoadedRef.current) setLoading(true);
    else setRefreshing(true);
    try {
      const token = getAuthToken();
      const queryParams = { ...queryFilters };
      const subIds = Array.isArray(queryParams.sub_register_id)
        ? queryParams.sub_register_id
        : [];
      if (subIds.length > 0) {
        queryParams.sub_register_id = subIds.join(',');
      } else {
        delete queryParams.sub_register_id;
      }

      // Drop empty filter strings so the API does not cast blank values
      Object.keys(queryParams).forEach((key) => {
        if (queryParams[key] === '' || queryParams[key] == null) {
          delete queryParams[key];
        }
      });

      if (isAwaiting) queryParams.status_id = '1';
      else if (isSoldOut) queryParams.status_id = '4';
      if (isMine) queryParams.my_listings = true;
      else if (isOwn) queryParams.my_properties = true;

      const res = await axios.get(`${API_URL}/admin/listings`, {
        headers: { Authorization: `Bearer ${token}` },
        params: queryParams,
        signal,
      });
      if (signal?.aborted) return;
      let rows = Array.isArray(res?.data?.listings)
        ? res.data.listings
        : (Array.isArray(res?.data) ? res.data : []);
      let totalCount = Number(res?.data?.total) || rows.length || 0;

      // Defense-in-depth: My Listings must only show this account's creations
      if (isMine) {
        const staff = getAuthUser();
        const asPartner = isPartnerRoute();
        rows = rows.filter((p) => isCreatedByLoggedInStaff(p, staff, asPartner));
        totalCount = rows.length;
      }

      setListings(rows);
      setTotal(totalCount);
      hasLoadedRef.current = true;
    } catch (error) {
      // Ignore aborted / superseded requests — they race when filters change
      if (
        signal?.aborted
        || error?.code === 'ERR_CANCELED'
        || error?.name === 'CanceledError'
        || error?.name === 'AbortError'
      ) {
        return;
      }
      console.error('Failed to fetch listings', error?.response?.data || error.message);
      setListings([]);
      setTotal(0);
    } finally {
      if (!signal?.aborted) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [queryFilters, isAwaiting, isSoldOut, isOwn, isMine]);

  // Text filters wait briefly so typing does not fire a request per keystroke.
  // Selects, chips, and paging update queryFilters immediately.
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedText((prev) => {
        if (
          prev.search === filters.search
          && prev.location_search === filters.location_search
          && prev.price_exact === filters.price_exact
          && prev.price_min === filters.price_min
          && prev.price_max === filters.price_max
        ) {
          return prev;
        }
        return {
          search: filters.search,
          location_search: filters.location_search,
          price_exact: filters.price_exact,
          price_min: filters.price_min,
          price_max: filters.price_max,
        };
      });
    }, 250);
    return () => clearTimeout(timer);
  }, [filters.search, filters.location_search, filters.price_exact, filters.price_min, filters.price_max]);

  // Reset when switching between All / Awaiting / Sold Out / Own listings
  useEffect(() => {
    hasLoadedRef.current = false;
    setListings([]);
    setTotal(0);
    setLoading(true);
    setRefreshing(false);
    setFilters(initialFilters);
    setDebouncedText({
      search: initialFilters.search,
      location_search: initialFilters.location_search,
      price_exact: initialFilters.price_exact,
      price_min: initialFilters.price_min,
      price_max: initialFilters.price_max,
    });
    setSubRegisterQuery('');
    setCitiesList([]);
    setSubRegistersList([]);
    setSelectedProperty(null);
    setShowPartnerModal(false);
  }, [listMode, initialFilters]);

  useEffect(() => {
    const controller = new AbortController();
    fetchListings(controller.signal);
    return () => controller.abort();
  }, [fetchListings]);

  useEffect(() => {
    if (!filtersOpen) return undefined;
    const onPointerDown = (event) => {
      if (filterPanelRef.current && !filterPanelRef.current.contains(event.target)) {
        setFiltersOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [filtersOpen]);

  const fetchStates = async () => {
    try {
      const res = await axios.get(`${API_URL}/states`);
      setStatesList(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error('Failed to fetch states');
      setStatesList([]);
    }
  };

  const fetchCities = async (stateId) => {
    try {
      const res = await axios.get(`${API_URL}/cities?state_id=${stateId}`);
      setCitiesList(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error('Failed to fetch cities');
      setCitiesList([]);
    }
  };

  const fetchSubRegisters = async (cityId) => {
    try {
      const res = await axios.get(`${API_URL}/subregisters?city_id=${cityId}`);
      setSubRegistersList(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error('Failed to fetch sub registers');
      setSubRegistersList([]);
    }
  };

  const handleStateChange = (e) => {
    const val = e.target.value;
    setFilters({ ...filters, state_id: val, city_id: '', sub_register_id: [], page: 1 });
    setSubRegisterQuery('');
    setSubRegistersList([]);
    if (val) fetchCities(val);
    else setCitiesList([]);
  };

  const handleCityChange = (e) => {
    const val = e.target.value;
    setSubRegisterQuery('');
    setFilters({ ...filters, city_id: val, sub_register_id: [], page: 1 });
    if (val) fetchSubRegisters(val);
    else setSubRegistersList([]);
  };

  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value, page: 1 });
  };

  const handleClearFilters = () => {
    setFilters(initialFilters);
    setDebouncedText({
      search: '',
      location_search: '',
      price_exact: '',
      price_min: '',
      price_max: '',
    });
    setSubRegisterQuery('');
    setCitiesList([]);
    setSubRegistersList([]);
  };

  const handlePriceExact = (value) => {
    setFilters({
      ...filters,
      price_exact: value,
      price_min: value ? '' : filters.price_min,
      price_max: value ? '' : filters.price_max,
      page: 1,
    });
  };

  const handlePriceRange = (key, value) => {
    setFilters({
      ...filters,
      [key]: value,
      price_exact: value ? '' : filters.price_exact,
      page: 1,
    });
  };

  const handleQuickUpdate = async (id, fields) => {
    const previous = listings;
    setListings((rows) => rows.map((row) => (
      String(row.id) === String(id) ? { ...row, ...fields } : row
    )));
    try {
      const token = getAuthToken();
      const res = await axios.put(`${API_URL}/admin/listings/${id}/quick-update`, fields, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Merge only updated keys (+ owner attribution) so creator_* from list enrich stay intact
      if (res?.data && typeof res.data === 'object') {
        const data = res.data;
        const patch = { ...fields };
        [
          'property_owner_name',
          'property_owner_number',
          'property_owner_number_added_by_role',
          'property_owner_number_added_by_name',
          'property_owner_number_added_by_id',
          'property_owner_number_added_at',
        ].forEach((key) => {
          if (data[key] !== undefined) patch[key] = data[key];
        });
        Object.keys(fields).forEach((key) => {
          if (data[key] !== undefined) patch[key] = data[key];
        });
        setListings((rows) => rows.map((row) => (
          String(row.id) === String(id) ? { ...row, ...patch } : row
        )));
      }
      toast.success('Updated successfully');
      return res?.data;
    } catch (error) {
      setListings(previous);
      toast.error(error.response?.data?.error || 'Update failed');
      throw error;
    }
  };

  const startOwnerEdit = (item) => {
    setOwnerEditId(item.id);
    setOwnerNameDraft(item.property_owner_name || '');
    setOwnerNumberDraft(item.property_owner_number || '');
  };

  const cancelOwnerEdit = () => {
    setOwnerEditId(null);
    setOwnerNameDraft('');
    setOwnerNumberDraft('');
  };

  const saveOwnerDetails = async (item) => {
    const nextName = String(ownerNameDraft || '').trim();
    const nextNumber = String(ownerNumberDraft || '').trim();
    const prevName = String(item.property_owner_name || '').trim();
    const prevNumber = String(item.property_owner_number || '').trim();
    const payload = {};
    if (nextName !== prevName) payload.property_owner_name = nextName;
    if (nextNumber !== prevNumber) payload.property_owner_number = nextNumber;
    if (Object.keys(payload).length === 0) {
      cancelOwnerEdit();
      return;
    }
    setSavingOwnerId(item.id);
    try {
      await handleQuickUpdate(item.id, payload);
      cancelOwnerEdit();
    } catch (_) {
      // toast already shown
    } finally {
      setSavingOwnerId(null);
    }
  };

  const handlePartnerRequest = async (property_id, request_type) => {
    try {
      const token = getAuthToken();
      await axios.post(`${API_URL}/admin/partner-requests`, {
        property_id,
        request_type
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${request_type.charAt(0).toUpperCase() + request_type.slice(1)} request submitted successfully`);
    } catch (error) {
      toast.error(error.response?.data?.error || `Failed to submit ${request_type} request`);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this listing? This action cannot be undone.')) {
      try {
        const token = getAuthToken();
        await axios.delete(`${API_URL}/admin/listings/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Deleted successfully');
        fetchListings();
      } catch (error) {
        toast.error('Delete failed');
      }
    }
  };

  const handleMultiSelect = (val) => {
    const id = Number(val);
    const current = (Array.isArray(filters.sub_register_id) ? filters.sub_register_id : []).map(Number);
    if (current.includes(id)) {
      setFilters({ ...filters, sub_register_id: current.filter((item) => item !== id), page: 1 });
    } else {
      setFilters({ ...filters, sub_register_id: [...current, id], page: 1 });
    }
  };

  const selectedSubIds = (Array.isArray(filters.sub_register_id) ? filters.sub_register_id : []).map(Number);
  const visibleSubRegisters = subRegistersList.filter((sr) => {
    const name = String(sr.sub_name || sr.sub_register_name || '').toLowerCase();
    return name.includes(subRegisterQuery.trim().toLowerCase());
  });
  const advancedCount = [
    !isAwaiting && !isSoldOut ? filters.status_id : '',
    filters.is_urgent,
    filters.is_highlighted,
    filters.is_draft,
    filters.state_id,
    filters.city_id,
    selectedSubIds.length ? 'subs' : '',
    filters.price_exact,
    filters.price_min,
    filters.price_max,
    filters.date_from,
    filters.date_to,
  ].filter(Boolean).length;

const propertySubId = Number(
  selectedProperty?.extra_fields?.sub_register_id
);




  return (
    <>
    <div className="admin-page property-listing-page">
      <div className="page-header flex-header">
        <div>
          <h1>{
            isAwaiting ? 'Awaiting Approval'
              : isSoldOut ? 'Sold Out Properties'
              : isMine ? 'My Listings'
              : isOwn ? 'Own Properties'
              : 'Property Listing'
          }</h1>
          <p>Total: {total} properties found</p>
        </div>
        <div className="header-actions">
          <button className="btn-primary">Create New</button>
        </div>
      </div>

      <div ref={filterPanelRef}>
      <div className="listing-toolbar">
        <div className="listing-search-bar">
          <label className="listing-search-field">
            <FiSearch />
            <input
              type="search"
              placeholder="Search ID or title..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              aria-label="Search by ID or title"
            />
          </label>
          <label className="listing-search-field">
            <FiSearch />
            <input
              type="search"
              placeholder="Area, address, or landmark..."
              value={filters.location_search}
              onChange={(e) => handleFilterChange('location_search', e.target.value)}
              aria-label="Search by area, address, or landmark"
            />
          </label>
          <button
            type="button"
            className={`btn-secondary filter-toggle${filtersOpen ? ' is-open' : ''}`}
            aria-expanded={filtersOpen}
            onClick={() => setFiltersOpen((open) => !open)}
          >
            <FiFilter />
            Advanced Filters
            {advancedCount > 0 && <span className="filter-count">{advancedCount}</span>}
          </button>
          <button type="button" className="btn-secondary" onClick={handleClearFilters}>
            <FiRefreshCcw /> Reset
          </button>
        </div>

        <section className="filter-section property-filter-section">
          <h4>Property</h4>
          <div className="filter-box-grid">
            <div className="filter-box">
              <label>Property Type</label>
              <select value={filters.is_project} onChange={(e) => handleFilterChange('is_project', e.target.value)}>
                <option value="">All Types</option>
                <option value="false">Property</option>
                <option value="true">Project</option>
              </select>
            </div>
            <div className="filter-box">
              <label>Listing Type</label>
              <select value={filters.listing_type} onChange={(e) => handleFilterChange('listing_type', e.target.value)}>
                <option value="">All Listing Types</option>
                <option value="Sale">Sale</option>
                <option value="Rent">Rent</option>
              </select>
            </div>
            <div className="filter-box">
              <label>Role / Ownership</label>
              <select value={filters.role} onChange={(e) => handleFilterChange('role', e.target.value)}>
                <option value="">All Ownerships</option>
                <option value="admin">Admin</option>
                <option value="partner">Partner</option>
                <option value="owner">Owner</option>
                <option value="agent">Agent</option>
              </select>
            </div>
          </div>
        </section>
      </div>

        {filtersOpen && (
          <div className="advanced-filters">
            <section className="filter-section">
              <h4>Status</h4>
              <div className="filter-box-grid">
                {!isAwaiting && !isSoldOut && (
                  <div className="filter-box">
                    <label>Status</label>
                    <select value={filters.status_id} onChange={(e) => handleFilterChange('status_id', e.target.value)}>
                      <option value="">All Statuses</option>
                      <option value="1">Awaiting</option>
                      <option value="2">Approved</option>
                      <option value="3">Rejected</option>
                      <option value="4">Sold Out</option>
                    </select>
                  </div>
                )}
                <div className="filter-box">
                  <label>Urgency</label>
                  <select value={filters.is_urgent} onChange={(e) => handleFilterChange('is_urgent', e.target.value)}>
                    <option value="">All</option>
                    <option value="true">Urgent</option>
                    <option value="false">Normal</option>
                  </select>
                </div>
                <div className="filter-box">
                  <label>Highlights</label>
                  <select value={filters.is_highlighted} onChange={(e) => handleFilterChange('is_highlighted', e.target.value)}>
                    <option value="">All</option>
                    <option value="true">Highlighted</option>
                    <option value="false">Standard</option>
                  </select>
                </div>
                <div className="filter-box">
                  <label>Draft</label>
                  <select value={filters.is_draft} onChange={(e) => handleFilterChange('is_draft', e.target.value)}>
                    <option value="">All</option>
                    <option value="true">Draft</option>
                    <option value="false">Published</option>
                  </select>
                </div>
              </div>
            </section>

            <section className="filter-section">
              <h4>Location</h4>
              <div className="filter-box-grid">
                <div className="filter-box">
                  <label>State</label>
                  <select value={filters.state_id} onChange={handleStateChange}>
                    <option value="">All States</option>
                    {statesList.map((s) => <option key={s.id} value={s.id}>{s.state_name}</option>)}
                  </select>
                </div>
                <div className="filter-box">
                  <label>City</label>
                  <select value={filters.city_id} onChange={handleCityChange} disabled={!filters.state_id}>
                    <option value="">All Cities</option>
                    {citiesList.map((c) => <option key={c.id} value={c.id}>{c.city_name}</option>)}
                  </select>
                </div>
                <div className="filter-box span-2">
                  <label>Sub Register</label>
                  <input
                    type="search"
                    className="sub-register-search"
                    placeholder={filters.city_id ? 'Search sub registers...' : 'Select a city first'}
                    value={subRegisterQuery}
                    onChange={(e) => setSubRegisterQuery(e.target.value)}
                    disabled={!filters.city_id}
                  />
                  <div className="multi-select-container">
                    {subRegistersList.length === 0 && (
                      <span className="filter-empty">Select a city to see sub registers.</span>
                    )}
                    {subRegistersList.length > 0 && visibleSubRegisters.length === 0 && (
                      <span className="filter-empty">No sub registers match that search.</span>
                    )}
                    {visibleSubRegisters.map((sr) => (
                      <button
                        type="button"
                        key={sr.id}
                        className={`multi-select-chip${selectedSubIds.includes(Number(sr.id)) ? ' active' : ''}`}
                        onClick={() => handleMultiSelect(sr.id)}
                      >
                        {sr.sub_name || sr.sub_register_name}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            <section className="filter-section">
              <h4>Price and date</h4>
              <div className="filter-box-grid">
                <div className="filter-box">
                  <label>Exact Price</label>
                  <input
                    type="number"
                    placeholder="Enter exact price..."
                    value={filters.price_exact}
                    onChange={(e) => handlePriceExact(e.target.value)}
                  />
                </div>
                <div className="filter-box range-box">
                  <label>Price Range</label>
                  <div className="range-inputs">
                    <input type="number" placeholder="Min" value={filters.price_min} onChange={(e) => handlePriceRange('price_min', e.target.value)} />
                    <span>-</span>
                    <input type="number" placeholder="Max" value={filters.price_max} onChange={(e) => handlePriceRange('price_max', e.target.value)} />
                  </div>
                </div>
                <div className="filter-box range-box span-2">
                  <label>Creation Date Range</label>
                  <div className="range-inputs">
                    <input type="date" value={filters.date_from} onChange={(e) => handleFilterChange('date_from', e.target.value)} />
                    <span>to</span>
                    <input type="date" value={filters.date_to} onChange={(e) => handleFilterChange('date_to', e.target.value)} />
                  </div>
                </div>
              </div>
            </section>
          </div>
        )}
      </div>

      <div className={`admin-card table-wrapper listing-results${refreshing ? ' is-refreshing' : ''}`}>
        {refreshing && <div className="listing-refresh-bar" aria-hidden="true" />}
        {loading && listings.length === 0 ? (
          <div className="skeleton" style={{height: '400px', width: '100%', borderRadius: '8px'}}></div>
        ) : (
          <>
            <table className="admin-hybrid-table">
              <thead>
                <tr>
                  <th>Property</th>
                  <th>Info</th>
                  <th>Created By</th>
                  <th>Status</th>
                  <th>Toggles</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
               {displayListings.length === 0 ? (
                  <tr><td colSpan="6" className="text-center py-4">No properties found.</td></tr>
                ) : displayListings.map(item => {
                  const roleBucket = resolveListingRoleBucket(item.role, item.user_listing_role);
                  const roleLabel = listingRoleLabel(item.role, item.user_listing_role);
                  const rowClass = [
                    'listing-row',
                    `listing-row--role-${roleBucket}`,
                    item.is_draft ? 'listing-row--draft' : '',
                  ].filter(Boolean).join(' ');
                  return (
                  <tr key={item.id} className={rowClass}>
                    <td>
                      <div className="hybrid-thumbnail" style={{ position: 'relative' }}
                        onMouseEnter={() => setHoveredId(item.id)}
                        onMouseLeave={() => setHoveredId(null)}
                      >
                        {item.is_draft && <span className="listing-draft-badge">Draft</span>}
                        <span className={`listing-role-badge listing-role-badge--${roleBucket}`}>
                          {roleLabel}
                        </span>
                        <img 
                          src={resolveListingThumb(item.thumbnail)}
                          alt="Thumbnail"
                          onError={e => { e.target.src = 'https://placehold.co/100x80?text=No+Image'; }}
                        />
                        <div className="hybrid-id">#{item.property_id || item.id}</div>
                      </div>
                 <div style={{ marginTop: '4px', display: 'flex', justifyContent: 'center' }}>
  <button
    title="View Partners"
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      fontSize: '0.8rem',
      padding: '6px 12px',
      background: '#2563eb',
      color: '#fff',
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
      whiteSpace: 'nowrap'
    }}
    onClick={() => {
      setSelectedProperty(item);
      setShowPartnerModal(true);
    }}
  >
    View
  </button>
</div>


                    </td>
                    <td>
                      <div className="hybrid-info">
                        <strong>
                          {(() => {
                            const fmtPrice = (n) => {
                              const num = Number(n);
                              if (!Number.isFinite(num) || num <= 0) return null;
                              return `₹${num.toLocaleString('en-IN')}`;
                            };
                            if (item.is_project) {
                              const from = fmtPrice(item.price_from);
                              const to = fmtPrice(item.price_to);
                              if (from || to) return `${from || '₹0'} - ${to || '₹0'}`;
                            }
                            const single = fmtPrice(item.price);
                            if (single) return single;
                            const from = fmtPrice(item.price_from);
                            const to = fmtPrice(item.price_to);
                            if (from || to) return `${from || '₹0'} - ${to || '₹0'}`;
                            return 'Price on Request';
                          })()}
                        </strong>
                        <p>{formatStateInCityFromIds(
                          item.state_id,
                          item.city_id,
                          Array.isArray(statesList) ? statesList : [],
                          Array.isArray(allCities) ? allCities : []
                        ) || '—'}</p>
                        <div className="listing-detail-lines">
                          {item.location ? <p><span>Area</span> {item.location}</p> : null}
                          {item.address ? <p><span>Address</span> {item.address}</p> : null}
                          {item.address_details ? <p><span>Landmark</span> {item.address_details}</p> : null}
                          {item.bhk ? <p><span>BHK</span> {item.bhk}</p> : null}
                          {item.furnishing ? <p><span>Furnishing</span> {furnishingLabel(item.furnishing, dropdowns)}</p> : null}
                          {item.land_area ? <p><span>Land</span> {item.land_area}{item.land_type ? ` ${item.land_type}` : ''}</p> : null}
                        </div>
                        <span className="info-pill">{item.is_project ? 'Project' : 'Property'} | {item.listing_type || 'Sale'}</span>
                        {item.is_draft && <span className="listing-draft-pill">Not published</span>}
                      </div>
                    </td>
                    <td>
                      <div className="hybrid-creator">
                        <p style={{ fontWeight: 600, fontSize: '0.9rem', marginBottom: '2px', color: '#1e293b' }}>
                          {item.creator_name && item.creator_name !== 'Unknown' ? item.creator_name : 'Unknown Creator'}
                        </p>
                        <p style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: '4px' }}>
                          {item.creator_phone ? item.creator_phone : 'No Phone'}
                        </p>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                          <span className={`badge-role badge-role--prominent ${roleBucket}`}>
                            {roleLabel}
                          </span>
                          {isUserListingRole(roleBucket) && (
                            <span className="listing-user-role-hint">User</span>
                          )}
                          {item.is_own_property && (roleBucket === 'admin' || roleBucket === 'partner') && (
                            <span style={{ 
                              fontSize: '0.7rem', 
                              fontWeight: '700', 
                              backgroundColor: '#f59e0b', 
                              color: '#fff', 
                              padding: '2px 6px', 
                              borderRadius: '4px' 
                            }}>
                              OWN
                            </span>
                          )}
                          <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                            {item.date || (item.createdAt ? new Date(item.createdAt).toLocaleDateString('en-IN') : '')}
                          </span>
                        </div>

                        <div className="listing-owner-meta">
                          <h4 className="listing-owner-meta__header">Property Owner/Reference</h4>
                          {ownerEditId === item.id ? (
                            <div className="listing-owner-number-edit listing-owner-number-edit--stack">
                              <input
                                type="text"
                                className="listing-owner-number-edit__input listing-owner-number-edit__input--wide"
                                value={ownerNameDraft}
                                onChange={(e) => setOwnerNameDraft(e.target.value)}
                                placeholder="Owner name"
                                autoFocus
                              />
                              <input
                                type="tel"
                                inputMode="numeric"
                                className="listing-owner-number-edit__input listing-owner-number-edit__input--wide"
                                value={ownerNumberDraft}
                                onChange={(e) => setOwnerNumberDraft(e.target.value.replace(/[^\d+\s-]/g, ''))}
                                placeholder="Owner number"
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    saveOwnerDetails(item);
                                  }
                                  if (e.key === 'Escape') cancelOwnerEdit();
                                }}
                              />
                              <div className="listing-owner-number-edit__row">
                                <button
                                  type="button"
                                  className="listing-owner-number-edit__save"
                                  disabled={savingOwnerId === item.id}
                                  onClick={() => saveOwnerDetails(item)}
                                >
                                  {savingOwnerId === item.id ? 'Saving…' : 'Save'}
                                </button>
                                <button
                                  type="button"
                                  className="listing-owner-number-edit__cancel"
                                  onClick={cancelOwnerEdit}
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <>
                              {item.property_owner_name ? (
                                <button
                                  type="button"
                                  className="listing-owner-name"
                                  onClick={() => startOwnerEdit(item)}
                                  title="Edit owner details"
                                >
                                  <span className="listing-owner-number__label">Name</span>
                                  <span>{item.property_owner_name}</span>
                                </button>
                              ) : null}

                              {item.property_owner_number ? (
                                <button
                                  type="button"
                                  className="listing-owner-number"
                                  title={ownerNumberTooltip(item)}
                                  onClick={() => startOwnerEdit(item)}
                                >
                                  <span className="listing-owner-number__label">Number</span>
                                  <span className="listing-owner-number__value">{item.property_owner_number}</span>
                                  <span className="listing-owner-number__tip" aria-hidden="true">
                                    {ownerNumberTooltip(item)}
                                  </span>
                                </button>
                              ) : null}

                              {!item.property_owner_name && !item.property_owner_number ? (
                                <button
                                  type="button"
                                  className="listing-owner-number-add"
                                  onClick={() => startOwnerEdit(item)}
                                >
                                  + Add Owner Name / Number
                                </button>
                              ) : !item.property_owner_name || !item.property_owner_number ? (
                                <button
                                  type="button"
                                  className="listing-owner-number-add"
                                  onClick={() => startOwnerEdit(item)}
                                >
                                  {!item.property_owner_name ? '+ Add Owner Name' : '+ Add Owner Number'}
                                </button>
                              ) : null}
                            </>
                          )}
                        </div>
                      </div>
                    </td>
                    <td>
                      <select 
                        className={`status-select status-${item.status_id ?? ''}`}
                        value={item.status_id == null ? '' : String(item.status_id)}
                        onChange={(e) => handleQuickUpdate(item.id, { status_id: e.target.value })}
                      >
                        <option value="1">Awaiting</option>
                        <option value="2">Approved</option>
                        <option value="3">Rejected</option>
                        <option value="4">Sold Out</option>
                      </select>
                    </td>
                    <td>
                      <div className="hybrid-toggles">
                        {isAdmin ? (
                          <>
                            <ListingToggle label="Urgent" checked={!!item.is_urgent} onToggle={() => handleQuickUpdate(item.id, { is_urgent: !item.is_urgent })} />
                            <ListingToggle label="Highlight" checked={!!item.is_highlighted} onToggle={() => handleQuickUpdate(item.id, { is_highlighted: !item.is_highlighted })} />
                            <ListingToggle label="Slider" checked={!!item.slider_show} onToggle={() => handleQuickUpdate(item.id, { slider_show: !item.slider_show })} />
                            <ListingToggle label="SliderWork" checked={!!item.slider_work} onToggle={() => handleQuickUpdate(item.id, { slider_work: !item.slider_work })} />
                            <ListingToggle label="DTCP" checked={!!item.is_dtcp} onToggle={() => handleQuickUpdate(item.id, { is_dtcp: !item.is_dtcp })} />
                          </>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                            <button 
                              className="btn-secondary" 
                              style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', width: '100%' }}
                              onClick={() => handlePartnerRequest(item.id, 'urgent')}
                              disabled={item.is_urgent}
                            >
                              {item.is_urgent ? 'Urgent (Approved)' : 'Req Urgent'}
                            </button>
                            <button 
                              className="btn-secondary" 
                              style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', width: '100%' }}
                              onClick={() => handlePartnerRequest(item.id, 'highlight')}
                              disabled={item.is_highlighted}
                            >
                              {item.is_highlighted ? 'Highlight (Approved)' : 'Req Highlight'}
                            </button>
                            <button 
                              className="btn-secondary" 
                              style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', width: '100%' }}
                              onClick={() => handlePartnerRequest(item.id, 'slider')}
                              disabled={item.slider_show}
                            >
                              {item.slider_show ? 'Slider (Approved)' : 'Req Slider'}
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="table-actions">
                        <button
                          type="button"
                          className="btn-icon"
                          title="View"
                          onClick={() => window.open(`/property/${item.id}`, '_blank')}
                        >
                          <FiEye />
                        </button>
                        <button
                          type="button"
                          className="btn-icon"
                          title="Edit"
                          onClick={() => {
                            const base = window.location.pathname.includes('/partner') ? '/partner' : '/admin';
                            const kind = (item.is_project === true || item.is_project === 1 || item.is_project === '1')
                              ? 'project'
                              : 'property';
                            navigate(`${base}/create-listing?editId=${item.id}&type=${kind}`);
                          }}
                        >
                          <FiEdit2 />
                        </button>
                        <button type="button" className="btn-icon btn-delete" style={{ color: '#ef4444' }} title="Delete" onClick={() => handleDelete(item.id)}><FiTrash2 /></button>
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Pagination Controls */}
            <div className="pagination-wrapper">
              <div className="pagination-info">
                Showing {(filters.page - 1) * filters.limit + 1} to {Math.min(filters.page * filters.limit, total)} of {total} results
              </div>
              <div className="pagination-controls">
                <select value={filters.limit} onChange={(e) => setFilters({...filters, limit: Number(e.target.value), page: 1})}>
                  <option value="10">10 per page</option>
                  <option value="25">25 per page</option>
                  <option value="50">50 per page</option>
                </select>
                <div className="pagination-buttons">
                  <button 
                    disabled={filters.page === 1} 
                    onClick={() => setFilters({...filters, page: filters.page - 1})}
                  >
                    <FiChevronLeft />
                  </button>
                  <span className="page-current">{filters.page}</span>
                  <button 
                    disabled={filters.page * filters.limit >= total} 
                    onClick={() => setFilters({...filters, page: filters.page + 1})}
                  >
                    <FiChevronRight />
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {!isAwaiting && (
        <AdminPropertyLocationsMap
          filters={filters}
          isAwaiting={isAwaiting}
          isOwn={isOwn}
          isMine={isMine}
          states={statesList}
          cities={allCities}
          subRegisters={allSubRegisters}
        />
      )}
    </div>
    
    
    {/* ✅ Modal must be INSIDE return */}
   {showPartnerModal && selectedProperty && (
  <div
    className="admin-modal-overlay"
    onClick={() => setShowPartnerModal(false)}
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "20px"
    }}
  >
    <div
      className="admin-modal"
      onClick={(e) => e.stopPropagation()}
      style={{
        width: "100%",
        maxWidth: "600px",
        maxHeight: "80vh",
        background: "#fff",
        borderRadius: "12px",
        boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden"
      }}
    >
      {/* Header */}
      <div
        className="modal-header"
        style={{
          padding: "14px 16px",
          borderBottom: "1px solid #e5e7eb",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center"
        }}
      >
        <h2 style={{ margin: 0, fontSize: "18px" }}>
          Property & Partner Details
        </h2>

        <button
          className="btn-icon"
          onClick={() => setShowPartnerModal(false)}
          style={{
            background: "transparent",
            border: "none",
            fontSize: "18px",
            cursor: "pointer"
          }}
        >
          <FiX />
        </button>
      </div>

      {/* Body */}
      
      
      {/* 🔹 Property Details Section */}


<div
  style={{
    border: "1px solid #e2e8f0",
    borderRadius: "10px",
    padding: "12px",
     width: "550px",
     margin: "0 auto 12px", 
    marginBottom: "10px",
    marginTop: "20px", 
    background: "#fafafa",
    
    
  }}
>
  {/* Header */}
  <div
    style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "6px"
    }}
  >
    <strong style={{ fontSize: "14px" }}>
      {selectedProperty?.title || "Property"}
    </strong>
  </div>

  {/* Type */}
  <p style={{ margin: "0 0 6px", fontSize: "13px", color: "#555" }}>
    Type: {selectedProperty?.is_project ? "Project" : "Property"}
  </p>

  {/* Sub Register */}
  <p style={{ fontSize: "12px", color: "#64748b", margin: "6px 0" }}>
    Sub Register:
  </p>

  <div
    style={{
      display: "flex",
      gap: "6px",
      flexWrap: "wrap"
    }}
  >
    <span
      style={{
        background: "#e2e8f0",
        padding: "3px 8px",
        borderRadius: "6px",
        fontSize: "11px"
      }}
    >
      {allSubRegisters.find(s => s.id === propertySubId)?.sub_name || "—"}
    </span>
  </div>
</div>
      
      <div
        className="modal-body"
        style={{
          padding: "16px",
          overflowY: "auto"
        }}
      >
        {getMatchingPartners().length > 0 ? (
          getMatchingPartners().map((p) => {
            const subNames = getSubRegisterNames(p.sub_registers || []);

            return (
              
              <div
                key={p.id}
                style={{
                  border: "1px solid #e2e8f0",
                  borderRadius: "10px",
                  padding: "12px",
                  marginBottom: "12px",
                  background: "#fafafa"
                }}
              >
                
                {/* Partner Header */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "6px"
                  }}
                >
                  <strong style={{ fontSize: "14px" }}>{p.name}</strong>
                </div>

                <p style={{ margin: "0 0 6px", fontSize: "13px", color: "#555" }}>
                  {p.phone}
                </p>

                <p style={{ fontSize: "12px", color: "#64748b", margin: "6px 0" }}>
                  Sub Registers:
                </p>

                {/* Sub registers */}
                <div
                  style={{
                    display: "flex",
                    gap: "6px",
                    flexWrap: "wrap"
                  }}
                >
                  {subNames.map((name, idx) => (
                    <span
                      key={idx}
                      style={{
                        background: "#e2e8f0",
                        padding: "3px 8px",
                        borderRadius: "6px",
                        fontSize: "11px"
                      }}
                    >
                      {name}
                    </span>
                  ))}
                </div>
              </div>
            );
          })
        ) : (
          <p style={{ textAlign: "center", color: "#888" }}>
            No partners available
          </p>
        )}
      </div>
    </div>
  </div>
)}
  </>
);
}
export default PropertyList;
